import {
  Stack,
  type StackProps,
  Typography,
  type TypographyProps
} from '@mui/material'
import React from 'react'

import Icon, { type IconProps } from 'components/Icon'
import { IntelligentHealthCheckVendorIcon } from 'components/status-checks/common/metrics/IntelligentHealthCheckVendorIcon'
import { type IntelligentHealthCheckType } from 'queries/intelligent-health-checks/types'
import theme from 'theme'

const ICON_SIZE = 20

const CIRCLE_ICON_CSS = {
  padding: 8,
  border: `1px solid ${theme.colors.gray[2]}`,
  borderRadius: '100%'
}

const SIZED_CIRCLE_ICON_CSS = {
  ...CIRCLE_ICON_CSS,
  width: ICON_SIZE,
  height: ICON_SIZE
}

interface ObserverTreeIconProps extends StackProps {
  ihcType?: IntelligentHealthCheckType
}

export function ConcurrentIcon({ css, ...props }) {
  return (
    <Icon
      {...props}
      css={[CIRCLE_ICON_CSS, { transform: 'scaleY(-1)' }, css]}
      icon="split"
    />
  )
}

export function ObserverTreeIcon({
  css,
  ihcType,
  ...props
}: ObserverTreeIconProps) {
  if (!ihcType) {
    return <ConcurrentIcon css={[CIRCLE_ICON_CSS, css]} {...props} />
  }

  return (
    <Stack
      {...props}
      css={[
        {
          ...SIZED_CIRCLE_ICON_CSS,
          alignItems: 'center',
          justifyContent: 'center'
        },
        css
      ]}
    >
      <IntelligentHealthCheckVendorIcon type={ihcType} width={ICON_SIZE} />
    </Stack>
  )
}

export function StatusChecksIcon(props: IconProps) {
  return <Icon {...props} css={SIZED_CIRCLE_ICON_CSS} icon="wavePulse" />
}

export function ExternalStepsIcon(props: IconProps) {
  return <Icon {...props} css={SIZED_CIRCLE_ICON_CSS} icon="gearCode" />
}

export function RollbackExternalStepsIcon(props: IconProps) {
  return <Icon {...props} css={SIZED_CIRCLE_ICON_CSS} icon="arrowRotateLeft" />
}

export function NodeListIcon(props: IconProps) {
  return <Icon {...props} css={SIZED_CIRCLE_ICON_CSS} icon="clipboardList" />
}

export function ActiveNodeIcon(props: IconProps) {
  return (
    <Icon
      {...props}
      css={SIZED_CIRCLE_ICON_CSS}
      icon="solidArrowsRotate"
      spinning={true}
    />
  )
}

export function LinearNodeIcon(props: IconProps) {
  return <Icon {...props} css={SIZED_CIRCLE_ICON_CSS} icon="arrowDown" />
}

export function NodeIcon(props: IconProps) {
  return <Icon {...props} css={CIRCLE_ICON_CSS} icon="arrowRight" />
}

export function NodeNumber({ children, css, ...props }: TypographyProps) {
  return (
    <Typography
      {...props}
      css={[
        {
          border: `1px solid ${theme.colors.gray[2]}`,
          borderRadius: '100%',
          padding: 3,
          minWidth: 30,
          textAlign: 'center'
        },
        css
      ]}
    >
      {children}
    </Typography>
  )
}
